/** Marks a grid cell's actual focusable DOM node so keyboard navigation can find it via `matches`/`closest`/`querySelectorAll`. */
export const GRID_CELL_SELECTOR = '[data-grid-cell]';

/**
 * The focusable data cells of a row, in column order. Skips the trailing
 * "remove row" cell, which has no `data-grid-cell` descendant.
 */
export function rowCells(tr: Element): HTMLElement[] {
    return Array.from(tr.querySelectorAll<HTMLElement>('td')).flatMap((td) => {
        const cell = td.querySelector<HTMLElement>(GRID_CELL_SELECTOR);
        return cell ? [cell] : [];
    });
}

export type NavKey =
    | 'ArrowUp'
    | 'ArrowDown'
    | 'ArrowLeft'
    | 'ArrowRight'
    | 'Enter'
    | 'Tab'
    | 'ShiftTab';

/** Resolve the cell a nav key should move focus to from `cell`, or `null` past a grid edge — arrows clamp at grid edges; Tab/Shift-Tab wrap rows. */
export function resolveNavigationTarget(
    cell: HTMLElement,
    key: NavKey,
): HTMLElement | null {
    const td = cell.closest('td');
    const tr = td?.closest('tr');
    if (!td || !tr) return null;

    const cells = rowCells(tr);
    const colIndex = cells.indexOf(cell);

    switch (key) {
        case 'ArrowUp':
        case 'ArrowDown': {
            const targetTr =
                key === 'ArrowUp'
                    ? tr.previousElementSibling
                    : tr.nextElementSibling;
            return targetTr ? (rowCells(targetTr)[colIndex] ?? null) : null;
        }
        case 'ArrowLeft':
            return cells[colIndex - 1] ?? null;
        case 'ArrowRight':
            return cells[colIndex + 1] ?? null;
        case 'Enter': {
            const targetTr = tr.nextElementSibling;
            return targetTr ? (rowCells(targetTr)[colIndex] ?? null) : null;
        }
        case 'Tab': {
            if (colIndex + 1 < cells.length) return cells[colIndex + 1];
            const nextTr = tr.nextElementSibling;
            return nextTr ? (rowCells(nextTr)[0] ?? null) : null;
        }
        case 'ShiftTab': {
            if (colIndex - 1 >= 0) return cells[colIndex - 1];
            const prevTr = tr.previousElementSibling;
            if (!prevTr) return null;
            const prevCells = rowCells(prevTr);
            return prevCells[prevCells.length - 1] ?? null;
        }
        default:
            return null;
    }
}

/** Whether ArrowLeft/Right should be left alone to move the native text caret instead of navigating cells. */
export function isTextCaretMove(
    target: EventTarget | null,
    key: 'ArrowLeft' | 'ArrowRight',
): boolean {
    if (!(target instanceof HTMLInputElement) || target.type !== 'text') {
        return false;
    }
    const { selectionStart, selectionEnd, value } = target;
    if (selectionStart == null || selectionEnd == null) return false;
    if (selectionStart !== selectionEnd) return true;
    return key === 'ArrowLeft' ? selectionStart > 0 : selectionEnd < value.length;
}

/** Whether `cell` is the last data cell of the last row (where Enter has nowhere to go). */
export function isLastGridCell(cell: HTMLElement): boolean {
    const tr = cell.closest('tr');
    if (!tr || tr.nextElementSibling) return false;
    const cells = rowCells(tr);
    return cells[cells.length - 1] === cell;
}
